import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Papa from "papaparse";
import ReactMarkdown from "react-markdown";
import styles from "../styles/VisualizePage.module.css";
import _ from "lodash";
import { downloadExampleFile } from "../api/api";
import { ChevronRight } from "lucide-react";

const VisualizePage = () => {
  const [example, setExample] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState("all");

  const { id } = useParams();

  const userAgents = ["customer", "user", "admin"];

  useEffect(() => {
    const getExample = async () => {
      try {
        const xample = await downloadExampleFile(id);
        setExample(xample);
        Papa.parse(xample, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          complete: (results) => {
            processData(results.data);
            setLoading(false);
          },
          error: (error) => {
            setError(`Error parsing CSV: ${error}`);
            setLoading(false);
          },
        });
      } catch (error) {
        if (error && typeof error === "object") {
          if (error.status !== 404) {
            console.error("Error fetching example file:", error);
          }
        }
        setLoading(false);
      }
    };

    getExample();
  }, [id]);

  const processData = (data) => {
    // Extract message data from events
    const processedMessages = data
      .filter((event) => event.event_name === "received_message")
      .map((event) => {
        try {
          const jsonState = JSON.parse(event.json_state || "{}");
          const message = jsonState.message || {};
          const timestamp = new Date(event.timestamp);

          return {
            id: event.id,
            eventType: event.event_name,
            sender: jsonState.sender || "",
            recipient: event.source_name,
            content:
              typeof message.content === "string"
                ? message.content
                : typeof message === "string"
                ? message
                : "",
            role: message.role || "",
            timestamp,
            formattedTime: timestamp.toLocaleTimeString(),
            agentType:
              message.role === "user" || userAgents.includes(jsonState.sender)
                ? "User"
                : event.agent_class_name,
          };
        } catch (e) {
          console.error("Error parsing JSON state:", e);
          return null;
        }
      })
      .filter((msg) => {
        return msg !== null;
      });

    // Sort by timestamp
    const sortedMessages = _.sortBy(processedMessages, "timestamp");

    // Create a map to track the first occurrence of each unique message content
    const messageMap = new Map();

    // Keep only the first occurrence of each unique message content
    const uniqueMessages = sortedMessages.filter((msg) => {
      // Skip empty messages
      if (!msg.content.trim()) {
        return false;
      }

      // If we haven't seen this message content before, keep it
      if (!messageMap.has(msg.content)) {
        messageMap.set(msg.content, true);
        return true;
      }

      // Otherwise, filter it out
      return false;
    });

    // Get unique agents
    const uniqueAgents = _.uniq([
      ...uniqueMessages.map((msg) => msg.agentType),
    ]).filter((agent) => agent);

    setMessages(uniqueMessages);
    setAgents(["all", ...uniqueAgents]);
  };

  const filterMessages = () => {
    if (selectedAgent === "all") {
      return messages;
    }
    return messages.filter((msg) => msg.agentType === selectedAgent);
  };

  // Function to get icon for an agent
  const getAgentIcon = (agentName) => {
    const iconMap = {
      AssistantAgent: (
        <img
          src="/icons/assistant@1x.webp"
          srcSet="/icons/assistant@2x.webp 2x, /icons/assistant@3x.webp 3x"
          alt="Assistant"
          className={styles.iconStandard}
        />
      ),
      CaptainUserProxyAgent: (
        <img
          src="/icons/captain@1x.webp"
          srcSet="/icons/captain@2x.webp 2x, /icons/captain@3x.webp 3x"
          alt="Captain"
          className={styles.iconStandard}
        />
      ),
      generic: (
        <img
          src="/icons/generic@1x.webp"
          srcSet="/icons/generic@2x.webp 2x, /icons/generic@3x.webp 3x"
          alt="Generic"
          className={styles.iconStandard}
        />
      ),
      GroupChatManager: (
        <img
          src="/icons/manager@1x.webp"
          srcSet="/icons/manager@2x.webp 2x, /icons/manager@3x.webp 3x"
          alt="GroupChatManager"
          className={styles.iconStandard}
        />
      ),
      RagAgent: (
        <img
          src="/icons/rag@1x.webp"
          srcSet="/icons/rag@2x.webp 2x, /icons/rag@3x.webp 3x"
          alt="RAG"
          className={styles.iconStandard}
        />
      ),
      ReasoningAgent: (
        <img
          src="/icons/reasoning@1x.webp"
          srcSet="/icons/reasoning@2x.webp 2x, /icons/reasoning@3x.webp 3x"
          alt="Reasoning"
          className={styles.iconStandard}
        />
      ),
      SwarmAgent: (
        <img
          src="/icons/swarm@1x.webp"
          srcSet="/icons/swarm@2x.webp 2x, /icons/swarm@3x.webp 3x"
          alt="Swarm"
          className={styles.iconStandard}
        />
      ),
      user: (
        <img
          src="/icons/user@1x.webp"
          srcSet="/icons/user@2x.webp 2x, /icons/user@3x.webp 3x"
          alt="User"
          className={styles.iconStandard}
        />
      ),
    };

    if (iconMap[agentName]) {
      return iconMap[agentName];
    } else {
      return iconMap.generic;
    }
  };

  if (loading) {
    return (
      <div className={`${styles.p4} ${styles.textCenter}`}>Loading data...</div>
    );
  }

  if (error) {
    return (
      <div className={`${styles.p4} ${styles.textCenter} ${styles.textRed500}`}>
        Error: {error}
      </div>
    );
  }

  const filteredMessages = filterMessages();

  // Color mapping for different agents
  const agentColors = {};
  agents.forEach((agent, index) => {
    if (agent !== "all") {
      // Generate colors based on index (excluding 'all')
      const hue = (index * 137) % 360; // Golden ratio to distribute colors nicely
      agentColors[agent] = `hsl(${hue}, 70%, 60%)`;
    }
  });

  if (!example) {
    return (
      <div>
        <p>No example yet</p>
      </div>
    );
  }

  return (
    <div className={`${styles.visualizePage}`}>
      <h1 className={`${styles.text2xl} ${styles.fontBold} ${styles.mb4}`}>
        Agent Message Exchange
      </h1>

      <div className={`${styles.mb4}`}>
        <label
          className={`${styles.block} ${styles.textSmWhite} ${styles.fontMedium} ${styles.mb1}`}
        >
          Filter by Agent:
        </label>
        <select
          value={selectedAgent}
          onChange={(e) => setSelectedAgent(e.target.value)}
          className={`${styles.wFull} ${styles.p2} ${styles.border} ${styles.rounded}`}
        >
          {agents.map((agent) => (
            <option key={agent} value={agent}>
              {agent || "(Unknown)"}
            </option>
          ))}
        </select>
      </div>

      <div className={`${styles.spaceY4}`}>
        {filteredMessages.length > 0 ? (
          filteredMessages.map((msg, index) => {
            // Only messages FROM the customer should be on the right
            const isFromUser = msg.agentType === "User";
            const bgColor = agentColors[msg.agentType] || "#e5e5e5";

            return (
              <div
                key={index}
                className={`${styles.flex} ${
                  isFromUser ? styles.justifyEnd : styles.justifyStart
                }
                } ${styles.wFull}`}
              >
                <div
                  className={`${styles.flex} ${
                    isFromUser ? styles.flexRowReverse : styles.flexRow
                  } ${styles.itemsStart} ${styles.gap2} ${styles.maxW3Xl}`}
                >
                  {/* Icon container */}
                  <div
                    className={`${styles.w8} ${styles.h8} ${styles.roundedFull} ${styles.flex} ${styles.itemsCenter} ${styles.justifyCenter} ${styles.flexShrink0}`}
                    style={{ backgroundColor: bgColor }}
                  >
                    {isFromUser
                      ? getAgentIcon("user")
                      : getAgentIcon(msg.agentType)}
                  </div>

                  {/* Message container */}
                  <div
                    className={`${styles.p3} ${styles.roundedLg} ${
                      styles.border
                    } ${isFromUser ? styles.bgBlue100 : styles.bgGray100}`}
                  >
                    <div
                      className={`${styles.flex} ${styles.justifyBetween} ${styles.textXs} ${styles.textGray600} ${styles.mb1}`}
                    >
                      <div
                        className={`${styles.fontSemibold} ${styles.flex} ${styles.itemsCenter}`}
                      >
                        {!isFromUser && (
                          <span className={`${styles.mr1}`}>
                            {msg.sender || "Unknown"}
                          </span>
                        )}
                        <ChevronRight size={12} className={`${styles.mx1}`} />
                        <span>{msg.recipient || "Unknown"}</span>
                      </div>
                      <div className={`${styles.ml4}`}>{msg.formattedTime}</div>
                    </div>
                    <div
                      className={`${styles.textSm} ${styles.whitespacePreWrap} ${styles.markdownContainer}`}
                    >
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div
            className={`${styles.textCenter} ${styles.p4} ${styles.bgGray100} ${styles.rounded}`}
          >
            No messages to display
          </div>
        )}
      </div>

      <div className={`${styles.mt6} ${styles.borderT} ${styles.pt4}`}>
        <h2 className={`${styles.textLg} ${styles.fontSemibold} ${styles.mb2}`}>
          Agent Icons Legend
        </h2>
        <div
          className={`${styles.grid} ${styles.gridCols2} ${styles.gridCols3} ${styles.gap4}`}
        >
          {agents
            .filter((agent) => agent !== "all" && agent)
            .map((agent) => (
              <div
                key={agent}
                className={`${styles.flex} ${styles.itemsCenter}`}
              >
                <div
                  className={`${styles.w8} ${styles.h8} ${styles.roundedFull} ${styles.flex} ${styles.itemsCenter} ${styles.justifyCenter} ${styles.mr2}`}
                  style={{ backgroundColor: agentColors[agent] }}
                >
                  {agent === "User"
                    ? getAgentIcon("user")
                    : getAgentIcon(agent)}
                </div>
                <span className={`${styles.textSmWhite} ${styles.truncate}`}>
                  {agent}
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default VisualizePage;
